import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './SalesRequestsManagement.module.css';
import AppLayout from '../components/AppLayout';
import { formatDateDDMMYYYY } from '../utils/dateFormat';
import {
  approveDiscount,
  deleteSalesRequest,
  getAllSalesRequests,
  updateSalesRequest,
} from '../api/salesRequestApi';

const translations = {
  en: {
    pageTitle: 'Sales Requests & Discount Approvals',
    explanationText:
      'Review sales requests, approve discounts, and manage pending order actions from one workspace.',
    failedFetch: 'Failed to fetch sales requests. Please try again.',
    failedUpdateRequest: 'Failed to update the sales request. Please try again.',
    failedDeleteRequest: 'Failed to delete the sales request. Please try again.',
    failedDiscountUpdate: 'Failed to update discount approval. Please try again.',
    noRequests: 'No requests match the current view.',
    requestId: 'Request ID',
    salesUser: 'Sales User',
    customer: 'Customer',
    customerId: 'Customer ID',
    date: 'Date',
    action: 'Action',
    status: 'Status',
    requestStatus: 'Request Status',
    payment: 'Payment',
    paymentStatus: 'Payment',
    dueDate: 'Due Date',
    subtotal: 'Order Subtotal',
    requestedDiscount: 'Requested Discount',
    requestedDiscountAmount: 'Requested Discount Amount',
    discountStatus: 'Discount Status',
    totalIfApproved: 'Total If Approved',
    currentInvoiceTotal: 'Current Invoice Total',
    invoiceTotal: 'Invoice Total',
    discountDecision: 'Discount Decision',
    discountDecisionRequired: 'Discount decision required',
    noDiscountDecision: 'No discount requested',
    discountApprovedMessage: 'Discount approved and applied to the linked invoice.',
    discountRejectedMessage: 'Discount rejected. The linked invoice remains without this discount.',
    discountPendingMessage: 'The linked invoice remains without this discount until a decision is made.',
    managerNotes: 'Manager Notes',
    noNotes: 'No manager notes.',
    items: 'Items',
    product: 'Product',
    sort: 'Sort',
    color: 'Color',
    quantity: 'Qty',
    unitPrice: 'Unit Price',
    lineTotal: 'Line Total',
    submissionDate: 'Desired Date',
    details: 'Details',
    hideDetails: 'Hide Details',
    acceptRelease: 'Accept & Release',
    acceptHold: 'Accept & Hold',
    changeDate: 'Change Date',
    reject: 'Reject',
    delete: 'Delete',
    approveDiscount: 'Approve Discount',
    rejectDiscount: 'Reject Discount',
    updateStatusTitle: 'Update Request Status',
    holdUntilDate: 'Hold Until Date',
    newSubmissionDate: 'New Submission Date',
    managerNotesLabel: 'Manager Notes',
    enterNotesPlaceholder: 'Enter notes (optional)',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirmDelete: 'Delete this request?',
    requestDeleted: 'Request deleted.',
    requestUpdated: 'Request updated.',
    discountUpdated: 'Discount decision saved.',
    pleaseSelectDate: 'Please select a new submission date.',
    homepage: 'Homepage',
    loading: 'Loading...',
    allQueues: 'All',
    needsAction: 'Needs Action',
    discountApprovals: 'Discount Approvals',
    heldReserved: 'Held / Reserved',
    processed: 'Processed',
    search: 'Search',
    searchPlaceholder: 'Request, customer, salesperson',
    salesperson: 'Salesperson',
    allSalespeople: 'All salespeople',
    paymentType: 'Payment Type',
    allPayments: 'All payments',
    cash: 'Cash',
    postpaid: 'Postpaid',
    allStatuses: 'All statuses',
    discountFilter: 'Discount',
    allDiscounts: 'All discounts',
    from: 'From',
    to: 'To',
    clearFilters: 'Clear',
    pending: 'Pending',
    dateChanged: 'Date Changed',
    accepted: 'Accepted',
    rejected: 'Rejected',
    held: 'Held',
    cancelled: 'Cancelled',
    reserved: 'Reserved',
    released: 'Released',
    unheld: 'Unheld',
    add: 'Add',
    remove: 'Remove',
    discountPending: 'Pending',
    discountApproved: 'Approved',
    discountRejected: 'Rejected',
    discountNotRequested: 'No Discount',
    unknownUser: 'Unknown User',
  },
  ar: {
    pageTitle: 'طلبات المبيعات واعتماد الخصومات',
    explanationText:
      'راجع طلبات المبيعات، واعتمد الخصومات، وأدر إجراءات الطلبات المعلقة من مكان واحد.',
    failedFetch: 'فشل في جلب طلبات المبيعات. حاول مرة أخرى.',
    failedUpdateRequest: 'فشل في تحديث الطلب. حاول مرة أخرى.',
    failedDeleteRequest: 'فشل حذف الطلب. حاول مرة أخرى.',
    failedDiscountUpdate: 'فشل تحديث اعتماد الخصم. حاول مرة أخرى.',
    noRequests: 'لا توجد طلبات تطابق العرض الحالي.',
    requestId: 'معرف الطلب',
    salesUser: 'مندوب المبيعات',
    customer: 'العميل',
    customerId: 'رقم العميل',
    date: 'التاريخ',
    action: 'الإجراء',
    status: 'الحالة',
    requestStatus: 'حالة الطلب',
    payment: 'الدفع',
    paymentStatus: 'الدفع',
    dueDate: 'تاريخ الاستحقاق',
    subtotal: 'إجمالي الطلب',
    requestedDiscount: 'الخصم المطلوب',
    requestedDiscountAmount: 'قيمة الخصم المطلوبة',
    discountStatus: 'حالة الخصم',
    totalIfApproved: 'الإجمالي في حالة الاعتماد',
    currentInvoiceTotal: 'إجمالي الفاتورة الحالي',
    invoiceTotal: 'إجمالي الفاتورة',
    discountDecision: 'قرار الخصم',
    discountDecisionRequired: 'مطلوب قرار للخصم',
    noDiscountDecision: 'لا يوجد خصم مطلوب',
    discountApprovedMessage: 'تم اعتماد الخصم وتطبيقه على الفاتورة المرتبطة.',
    discountRejectedMessage: 'تم رفض الخصم. الفاتورة المرتبطة ستبقى بدون هذا الخصم.',
    discountPendingMessage: 'الفاتورة المرتبطة ستبقى بدون هذا الخصم حتى يتم اتخاذ قرار.',
    managerNotes: 'ملاحظات المدير',
    noNotes: 'لا توجد ملاحظات.',
    items: 'العناصر',
    product: 'المنتج',
    sort: 'الصنف',
    color: 'اللون',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'إجمالي السطر',
    submissionDate: 'التاريخ المطلوب',
    details: 'التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    acceptRelease: 'قبول وصرف',
    acceptHold: 'قبول وحجز',
    changeDate: 'تغيير التاريخ',
    reject: 'رفض',
    delete: 'حذف',
    approveDiscount: 'اعتماد الخصم',
    rejectDiscount: 'رفض الخصم',
    updateStatusTitle: 'تحديث حالة الطلب',
    holdUntilDate: 'الحجز حتى تاريخ',
    newSubmissionDate: 'تاريخ تقديم جديد',
    managerNotesLabel: 'ملاحظات المدير',
    enterNotesPlaceholder: 'اكتب الملاحظات (اختياري)',
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirmDelete: 'حذف هذا الطلب؟',
    requestDeleted: 'تم حذف الطلب.',
    requestUpdated: 'تم تحديث الطلب.',
    discountUpdated: 'تم حفظ قرار الخصم.',
    pleaseSelectDate: 'يرجى اختيار تاريخ تقديم جديد.',
    homepage: 'الصفحة الرئيسية',
    loading: 'جار التحميل...',
    allQueues: 'الكل',
    needsAction: 'يحتاج إجراء',
    discountApprovals: 'اعتماد الخصومات',
    heldReserved: 'محجوز',
    processed: 'تمت المعالجة',
    search: 'بحث',
    searchPlaceholder: 'طلب، عميل، مندوب',
    salesperson: 'مندوب المبيعات',
    allSalespeople: 'كل المندوبين',
    paymentType: 'نوع الدفع',
    allPayments: 'كل طرق الدفع',
    cash: 'كاش',
    postpaid: 'آجل',
    allStatuses: 'كل الحالات',
    discountFilter: 'الخصم',
    allDiscounts: 'كل الخصومات',
    from: 'من',
    to: 'إلى',
    clearFilters: 'مسح',
    pending: 'قيد الانتظار',
    dateChanged: 'تم تغيير التاريخ',
    accepted: 'مقبول',
    rejected: 'مرفوض',
    held: 'محتجز',
    cancelled: 'ملغي',
    reserved: 'محجوز',
    released: 'مصروف',
    unheld: 'غير محجوز',
    add: 'إضافة',
    remove: 'سحب',
    discountPending: 'قيد الانتظار',
    discountApproved: 'معتمد',
    discountRejected: 'مرفوض',
    discountNotRequested: 'بدون خصم',
    unknownUser: 'مستخدم غير معروف',
  },
};

const queueDefinitions = [
  { key: 'needsAction', labelKey: 'needsAction' },
  { key: 'discountApprovals', labelKey: 'discountApprovals' },
  { key: 'heldReserved', labelKey: 'heldReserved' },
  { key: 'processed', labelKey: 'processed' },
  { key: 'all', labelKey: 'allQueues' },
];

const statusClassMap = {
  pending: 'statusChipPending',
  'date-changed': 'statusChipDateChanged',
  accepted: 'statusChipAccepted',
  rejected: 'statusChipRejected',
  held: 'statusChipHeld',
  cancelled: 'statusChipCancelled',
};

const discountClassMap = {
  pending: 'discountChipPending',
  approved: 'discountChipApproved',
  rejected: 'discountChipRejected',
  'not-requested': 'discountChipNone',
};

const processedStatuses = ['accepted', 'rejected', 'held', 'cancelled'];

const parseTokenPayload = () => {
  const token = localStorage.getItem('authToken');
  if (!token) return { userType: null, requesterUsername: null };
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userType: payload.userType,
      requesterUsername: payload.username,
    };
  } catch (error) {
    console.error('Error parsing token payload:', error);
    return { userType: null, requesterUsername: null };
  }
};

const SalesRequestsManagement = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = useCallback((key) => translations[language][key] || key, [language]);
  const locale = language === 'ar' ? 'ar-EG' : 'en-US';
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editRequestId, setEditRequestId] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editAction, setEditAction] = useState('');
  const [updatedSubmissionDate, setUpdatedSubmissionDate] = useState('');
  const [notes, setNotes] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [activeQueue, setActiveQueue] = useState('needsAction');
  const [filters, setFilters] = useState({
    search: '',
    salesUserId: 'all',
    status: 'all',
    paymentType: 'all',
    discountStatus: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const userInfo = useMemo(parseTokenPayload, []);
  const canApproveDiscount = ['manager', 'sales-manager'].includes(userInfo.userType);
  const canManageRequests = ['manager', 'admin'].includes(userInfo.userType);

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  const formatDate = (value) => {
    if (!value) return '—';
    return formatDateDDMMYYYY(value, '—');
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('pending'),
      'date-changed': t('dateChanged'),
      accepted: t('accepted'),
      rejected: t('rejected'),
      held: t('held'),
      cancelled: t('cancelled'),
    };
    return labels[status] || status || '—';
  };

  const getDiscountLabel = (status) => {
    const labels = {
      pending: t('discountPending'),
      approved: t('discountApproved'),
      rejected: t('discountRejected'),
      'not-requested': t('discountNotRequested'),
    };
    return labels[status] || t('discountNotRequested');
  };

  const getActionLabel = (action) => {
    if (action === 'add') return t('add');
    if (action === 'remove') return t('remove');
    return action || '—';
  };

  const getPaymentLabel = (paymentType) =>
    paymentType === 'postpaid' ? t('postpaid') : t('cash');

  const getCurrentInvoiceTotal = (request) => {
    const summary = request.orderSummary || {};
    if (request.discountApprovalStatus === 'approved') {
      return Number(summary.totalAfterDiscount) || 0;
    }
    return Number(summary.subtotal) || 0;
  };

  const getDiscountDecisionMessage = (request) => {
    if (request.discountApprovalStatus === 'approved') return t('discountApprovedMessage');
    if (request.discountApprovalStatus === 'rejected') return t('discountRejectedMessage');
    if (request.discountApprovalStatus === 'pending') return t('discountPendingMessage');
    return t('noDiscountDecision');
  };

  const isProcessed = (request) => processedStatuses.includes(request.status);

  const belongsToQueue = useCallback((request, queue) => {
    if (queue === 'all') return true;
    if (queue === 'needsAction') {
      return ['pending', 'date-changed'].includes(request.status);
    }
    if (queue === 'discountApprovals') {
      return request.discountApprovalStatus === 'pending';
    }
    if (queue === 'heldReserved') {
      return request.status === 'held' || request.releaseStatus === 'reserved';
    }
    if (queue === 'processed') {
      return isProcessed(request);
    }
    return true;
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await getAllSalesRequests();
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching sales requests:', error);
      setErrorMessage(t('failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const salesOptions = useMemo(() => {
    const map = new Map();
    requests.forEach((request) => {
      const id = request.salesUser?._id;
      if (id) {
        map.set(id, request.salesUser?.username || t('unknownUser'));
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [requests, locale, t]);

  const filteredRequests = useMemo(() => {
    const normalizedSearch = filters.search.trim().toLowerCase();
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    if (toDate) toDate.setHours(23, 59, 59, 999);

    return requests.filter((request) => {
      if (!belongsToQueue(request, activeQueue)) return false;
      if (filters.salesUserId !== 'all' && request.salesUser?._id !== filters.salesUserId) {
        return false;
      }
      if (filters.status !== 'all' && request.status !== filters.status) return false;
      if (filters.paymentType !== 'all' && request.paymentType !== filters.paymentType) return false;
      if (
        filters.discountStatus !== 'all' &&
        (request.discountApprovalStatus || 'not-requested') !== filters.discountStatus
      ) {
        return false;
      }
      if (fromDate || toDate) {
        const created = new Date(request.createdAt);
        if (Number.isNaN(created.getTime())) return false;
        if (fromDate && created < fromDate) return false;
        if (toDate && created > toDate) return false;
      }
      if (!normalizedSearch) return true;
      const customerName = request.customer?.fullName || request.customerSnapshot?.fullName || '';
      const customerId = request.customer?.customerID || request.customerSnapshot?.customerID || '';
      const haystack = [
        request._id,
        customerName,
        customerId,
        request.salesUser?.username,
        request.status,
        request.paymentType,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [activeQueue, belongsToQueue, filters, requests]);

  const queueCounts = useMemo(() => {
    return queueDefinitions.reduce((acc, queue) => {
      acc[queue.key] = requests.filter((request) => belongsToQueue(request, queue.key)).length;
      return acc;
    }, {});
  }, [belongsToQueue, requests]);

  const hasFilters =
    filters.search ||
    filters.salesUserId !== 'all' ||
    filters.status !== 'all' ||
    filters.paymentType !== 'all' ||
    filters.discountStatus !== 'all' ||
    filters.dateFrom ||
    filters.dateTo;

  const updateFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      salesUserId: 'all',
      status: 'all',
      paymentType: 'all',
      discountStatus: 'all',
      dateFrom: '',
      dateTo: '',
    });
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const openStatusModal = (id, status, action) => {
    setEditRequestId(id);
    setEditStatus(status);
    setEditAction(action);
    setUpdatedSubmissionDate('');
    setNotes('');
  };

  const closeStatusModal = () => {
    setEditRequestId(null);
    setEditStatus('');
    setEditAction('');
    setUpdatedSubmissionDate('');
    setNotes('');
  };

  const handleSaveStatus = async () => {
    if (editStatus === 'date-changed' && !updatedSubmissionDate) {
      setErrorMessage(t('pleaseSelectDate'));
      return;
    }

    const payload = {
      status: editStatus,
      notes: notes || undefined,
    };
    if (editStatus === 'date-changed') {
      payload.updatedSubmissionDate = updatedSubmissionDate;
    }
    if (editStatus === 'held' && editAction === 'add') {
      payload.status = 'accepted';
    }

    try {
      setLoading(true);
      const response = await updateSalesRequest(editRequestId, payload);
      setRequests((prev) =>
        prev.map((request) => (request._id === editRequestId ? response.data : request))
      );
      setModalMessage(t('requestUpdated'));
      closeStatusModal();
    } catch (error) {
      console.error('Error updating sales request:', error);
      setErrorMessage(t('failedUpdateRequest'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setLoading(true);
      await deleteSalesRequest(confirmDeleteId);
      setRequests((prev) => prev.filter((request) => request._id !== confirmDeleteId));
      setModalMessage(t('requestDeleted'));
      setConfirmDeleteId(null);
    } catch (error) {
      console.error('Error deleting sales request:', error);
      setErrorMessage(t('failedDeleteRequest'));
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountAction = async (requestId, action) => {
    try {
      setLoading(true);
      const response = await approveDiscount(requestId, action);
      setRequests((prev) =>
        prev.map((request) => (request._id === requestId ? response.data : request))
      );
      setModalMessage(t('discountUpdated'));
    } catch (error) {
      console.error('Error updating discount:', error);
      setErrorMessage(t('failedDiscountUpdate'));
    } finally {
      setLoading(false);
    }
  };

  const renderRequestCard = (request) => {
    const expanded = expandedIds.has(request._id);
    const customerName =
      request.customer?.fullName || request.customerSnapshot?.fullName || 'N/A';
    const customerCode =
      request.customer?.customerID || request.customerSnapshot?.customerID || 'N/A';
    const summary = request.orderSummary || {};
    const canActOnStatus = ['pending', 'date-changed'].includes(request.status);
    const hasPendingDiscount = request.discountApprovalStatus === 'pending';
    const hasRequestedDiscount = Number(request.requestedDiscountPercent || 0) > 0;
    const currentInvoiceTotal = getCurrentInvoiceTotal(request);

    return (
      <article key={request._id} className={styles.queueCard}>
        <button
          type="button"
          className={styles.queueCardSummary}
          onClick={() => toggleExpanded(request._id)}
          aria-expanded={expanded}
        >
          <div className={styles.queueMain}>
            <span className={styles.cardId}>#{request._id}</span>
            <strong>{customerName}</strong>
            <span>{customerCode}</span>
          </div>
          <div className={styles.queueMeta}>
            <span className={styles.salesUserBadge}>
              {t('salesUser')}: {request.salesUser?.username || t('unknownUser')}
            </span>
            <span className={`${styles.statusChip} ${styles[statusClassMap[request.status]] || ''}`}>
              {t('requestStatus')}: {getStatusLabel(request.status)}
            </span>
            <span className={`${styles.discountChip} ${styles[discountClassMap[request.discountApprovalStatus]] || styles.discountChipNone}`}>
              {t('discountStatus')}: {getDiscountLabel(request.discountApprovalStatus)}
            </span>
            <span className={styles.invoiceBadge}>
              {t('invoiceTotal')}: {formatCurrency(currentInvoiceTotal)}
            </span>
            <span className={styles.expandLabel}>{expanded ? t('hideDetails') : t('details')}</span>
          </div>
        </button>

        {expanded && (
          <div className={styles.queueCardDetails}>
            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('date')}</span>
                <span className={styles.metaValue}>{formatDate(request.createdAt)}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('action')}</span>
                <span className={styles.metaValue}>{getActionLabel(request.action)}</span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('payment')}</span>
                <span className={styles.metaValue}>{getPaymentLabel(request.paymentType)}</span>
              </div>
              {request.paymentType === 'postpaid' && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t('dueDate')}</span>
                  <span className={styles.metaValue}>{formatDate(request.paymentDueDate)}</span>
                </div>
              )}
              <div className={`${styles.metaItem} ${styles.fullWidth}`}>
                <span className={styles.metaLabel}>{t('managerNotes')}</span>
                <span className={styles.metaValue}>
                  {request.managerResponse?.notes || t('noNotes')}
                </span>
              </div>
            </div>

            <div className={styles.financialSummary}>
              <div>
                <span>{t('subtotal')}</span>
                <strong>{formatCurrency(summary.subtotal)}</strong>
              </div>
              <div>
                <span>{t('requestedDiscount')}</span>
                <strong>{Number(request.requestedDiscountPercent || 0)}%</strong>
              </div>
              <div>
                <span>{t('requestedDiscountAmount')}</span>
                <strong>{formatCurrency(summary.discountAmount)}</strong>
              </div>
              <div>
                <span>{t('totalIfApproved')}</span>
                <strong>{formatCurrency(summary.totalAfterDiscount)}</strong>
              </div>
              <div className={styles.currentInvoiceTile}>
                <span>{t('currentInvoiceTotal')}</span>
                <strong>{formatCurrency(currentInvoiceTotal)}</strong>
              </div>
            </div>

            <section
              className={`${styles.discountDecisionPanel} ${
                hasPendingDiscount ? styles.discountDecisionPending : ''
              }`}
            >
              <div className={styles.discountDecisionCopy}>
                <span className={styles.decisionEyebrow}>{t('discountDecision')}</span>
                <h3>
                  {hasPendingDiscount
                    ? t('discountDecisionRequired')
                    : getDiscountLabel(request.discountApprovalStatus)}
                </h3>
                <p>{getDiscountDecisionMessage(request)}</p>
              </div>
              <div className={styles.discountDecisionStatus}>
                <span className={`${styles.discountChip} ${styles[discountClassMap[request.discountApprovalStatus]] || styles.discountChipNone}`}>
                  {t('discountStatus')}: {getDiscountLabel(request.discountApprovalStatus)}
                </span>
                {hasRequestedDiscount && (
                  <span className={styles.requestedDiscountPill}>
                    {t('requestedDiscount')}: {Number(request.requestedDiscountPercent || 0)}%
                  </span>
                )}
              </div>
              {canApproveDiscount && hasPendingDiscount && (
                <div className={styles.discountDecisionActions}>
                  <button
                    type="button"
                    className={styles.approveDecisionButton}
                    onClick={() => handleDiscountAction(request._id, 'approve')}
                    disabled={loading}
                  >
                    {t('approveDiscount')}
                  </button>
                  <button
                    type="button"
                    className={styles.rejectDecisionButton}
                    onClick={() => handleDiscountAction(request._id, 'reject')}
                    disabled={loading}
                  >
                    {t('rejectDiscount')}
                  </button>
                </div>
              )}
            </section>

            <div className={styles.itemsTable}>
              <div className={styles.itemsTableHeader}>
                <span>#</span>
                <span>{t('product')}</span>
                <span>{t('sort')}</span>
                <span>{t('color')}</span>
                <span>{t('quantity')}</span>
                <span>{t('unitPrice')}</span>
                <span>{t('lineTotal')}</span>
                <span>{t('submissionDate')}</span>
              </div>
              {request.items?.map((item, index) => (
                <div key={`${request._id}-${index}`} className={styles.itemsRow}>
                  <span>{index + 1}</span>
                  <span>{item.product}</span>
                  <span>{item.sortName}</span>
                  <span>{item.color}</span>
                  <span>{item.quantity}</span>
                  <span>{formatCurrency(item.unitPrice)}</span>
                  <span>{formatCurrency(item.lineTotal)}</span>
                  <span>{formatDate(item.itemSubmissionDate)}</span>
                </div>
              ))}
            </div>

            {canManageRequests && (
              <div className={styles.cardActions}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => openStatusModal(request._id, 'accepted', request.action)}
                  disabled={!canActOnStatus}
                >
                  {t('acceptRelease')}
                </button>
                {request.action === 'remove' && (
                  <button
                    type="button"
                    className={styles.accentButton}
                    onClick={() => openStatusModal(request._id, 'held', request.action)}
                    disabled={!canActOnStatus}
                  >
                    {t('acceptHold')}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.neutralButton}
                  onClick={() => openStatusModal(request._id, 'date-changed', request.action)}
                  disabled={!canActOnStatus}
                >
                  {t('changeDate')}
                </button>
                <button
                  type="button"
                  className={styles.warningButton}
                  onClick={() => openStatusModal(request._id, 'rejected', request.action)}
                  disabled={!canActOnStatus}
                >
                  {t('reject')}
                </button>
                <button
                  type="button"
                  className={styles.ghostButton}
                  onClick={() => setConfirmDeleteId(request._id)}
                >
                  {t('delete')}
                </button>
              </div>
            )}
          </div>
        )}
      </article>
    );
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      subtitle={t('explanationText')}
      language={language}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.page}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p>{t('loading')}</p>
        </div>
      )}

      <section className={styles.content}>
        {errorMessage && <div className={styles.errorBanner}>{errorMessage}</div>}

        <div className={styles.queueTabs}>
          {queueDefinitions.map((queue) => (
            <button
              type="button"
              key={queue.key}
              className={`${styles.queueTab} ${activeQueue === queue.key ? styles.queueTabActive : ''}`}
              onClick={() => setActiveQueue(queue.key)}
            >
              <span>{t(queue.labelKey)}</span>
              <strong>{queueCounts[queue.key] || 0}</strong>
            </button>
          ))}
        </div>

        <section className={styles.filtersPanel}>
          <label className={styles.filterField}>
            <span>{t('search')}</span>
            <input
              type="search"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder={t('searchPlaceholder')}
            />
          </label>
          <label className={styles.filterField}>
            <span>{t('salesperson')}</span>
            <select
              value={filters.salesUserId}
              onChange={(event) => updateFilter('salesUserId', event.target.value)}
            >
              <option value="all">{t('allSalespeople')}</option>
              {salesOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span>{t('status')}</span>
            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
              <option value="all">{t('allStatuses')}</option>
              <option value="pending">{t('pending')}</option>
              <option value="date-changed">{t('dateChanged')}</option>
              <option value="accepted">{t('accepted')}</option>
              <option value="rejected">{t('rejected')}</option>
              <option value="held">{t('held')}</option>
              <option value="cancelled">{t('cancelled')}</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span>{t('paymentType')}</span>
            <select value={filters.paymentType} onChange={(event) => updateFilter('paymentType', event.target.value)}>
              <option value="all">{t('allPayments')}</option>
              <option value="cash">{t('cash')}</option>
              <option value="postpaid">{t('postpaid')}</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span>{t('discountFilter')}</span>
            <select value={filters.discountStatus} onChange={(event) => updateFilter('discountStatus', event.target.value)}>
              <option value="all">{t('allDiscounts')}</option>
              <option value="pending">{t('discountPending')}</option>
              <option value="approved">{t('discountApproved')}</option>
              <option value="rejected">{t('discountRejected')}</option>
              <option value="not-requested">{t('discountNotRequested')}</option>
            </select>
          </label>
          <label className={styles.filterField}>
            <span>{t('from')}</span>
            <input type="date" value={filters.dateFrom} onChange={(event) => updateFilter('dateFrom', event.target.value)} />
          </label>
          <label className={styles.filterField}>
            <span>{t('to')}</span>
            <input type="date" value={filters.dateTo} onChange={(event) => updateFilter('dateTo', event.target.value)} />
          </label>
          <button type="button" className={styles.clearButton} onClick={clearFilters} disabled={!hasFilters}>
            {t('clearFilters')}
          </button>
        </section>

        {filteredRequests.length === 0 ? (
          <div className={styles.emptyState}>{t('noRequests')}</div>
        ) : (
          <section className={styles.queueList}>
            {filteredRequests.map(renderRequestCard)}
          </section>
        )}
      </section>

      {editStatus && editRequestId && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h2>{t('updateStatusTitle')}</h2>
            {editStatus === 'date-changed' && (
              <label className={styles.modalLabel}>
                {t('newSubmissionDate')}
                <input
                  type="date"
                  value={updatedSubmissionDate}
                  onChange={(event) => setUpdatedSubmissionDate(event.target.value)}
                  className={styles.modalInput}
                  required
                />
              </label>
            )}
            {editStatus === 'held' && (
              <label className={styles.modalLabel}>
                {t('holdUntilDate')}
                <input
                  type="date"
                  value={updatedSubmissionDate}
                  onChange={(event) => setUpdatedSubmissionDate(event.target.value)}
                  className={styles.modalInput}
                />
              </label>
            )}
            <label className={styles.modalLabel}>
              {t('managerNotesLabel')}
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={t('enterNotesPlaceholder')}
                className={`${styles.modalInput} ${styles.textArea}`}
              />
            </label>
            <div className={styles.modalActions}>
              <button type="button" className={styles.primaryButton} onClick={handleSaveStatus}>
                {t('save')}
              </button>
              <button type="button" className={styles.secondaryButton} onClick={closeStatusModal}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteId && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <p>{t('confirmDelete')}</p>
            <div className={styles.modalActions}>
              <button type="button" className={styles.warningButton} onClick={handleDelete}>
                {t('delete')}
              </button>
              <button type="button" className={styles.secondaryButton} onClick={() => setConfirmDeleteId(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <p>{modalMessage}</p>
            <button type="button" className={styles.primaryButton} onClick={() => setModalMessage('')}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default SalesRequestsManagement;
